// src/client/components/profile-manager/detail-view/RelationsSection.tsx
// ✅ FINAL FIX: Use mutations from parent component

import { useState } from 'react';
import { Button } from '@client/components/ui/button';
import { Pencil } from 'lucide-react';
import { InlineCategoryEditor } from '../InlineCategoryEditor';
import type { ProfileWithRelations, Category, List } from '@shared/types';

interface RelationsSectionProps {
  profile: ProfileWithRelations;
  categories: Category[];
  lists: List[];
  onUpdate: (updates: Partial<ProfileWithRelations>) => Promise<void>;
}

export default function RelationsSection({
  profile,
  categories,
  lists,
  onUpdate,
}: RelationsSectionProps) {
  const [isEditingCategories, setIsEditingCategories] = useState(false);

  // ✅ FINAL FIX: Call onUpdate to trigger parent refetch
  const handleCategoriesUpdate = async (profileId: number, newCategoryIds: number[]) => {
    // Just close the editor - parent component handles the API call
    setIsEditingCategories(false);
    
    // Trigger parent to refetch profile data
    await onUpdate({});
  };

  return (
    <div className="border rounded-lg p-4 bg-white">
      <h3 className="text-lg font-semibold mb-4">Relations</h3>
      
      {/* Categories */}
      <div className="py-2 border-b">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Categories:
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingCategories(!isEditingCategories)}
          >
            <Pencil className="h-3 w-3 mr-1" />
            {isEditingCategories ? 'Cancel' : 'Edit'}
          </Button>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {profile.categories && profile.categories.length > 0 ? (
            profile.categories.map((cat) => (
              <span
                key={cat.categoryId}
                className="px-2 py-1 text-xs rounded"
                style={{
                  backgroundColor: cat.categoryColor || '#e5e7eb',
                  color: '#000',
                }}
              >
                {cat.categoryName}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400 italic">
              No categories
            </span>
          )}
        </div>
        
        {isEditingCategories && (
          <div className="mt-2">
            <InlineCategoryEditor
              profileId={profile.id}
              currentCategories={profile.categories || []}
              allCategories={categories}
              onSave={handleCategoriesUpdate}
              onCancel={() => setIsEditingCategories(false)}
              isOpen={isEditingCategories}
              onOpenChange={setIsEditingCategories}
            />
          </div>
        )}
      </div>
      
      {/* Lists */}
      <div className="py-2">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            Lists:
          </label>
          {/* TODO: Add List Editor (same pattern as categories) */}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {profile.lists && profile.lists.length > 0 ? (
            profile.lists.map((list) => (
              <span
                key={list.listId}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded"
              >
                {list.listName}
              </span>
            ))
          ) : (
            <span className="text-sm text-gray-400 italic">
              No lists
            </span>
          )}
        </div>
      </div>
    </div>
  );
}